/**
 * A sheet of QR labels, for a label printer rather than one at a time.
 *
 * Labelling 67 moulds one dialog at a time is 67 print dialogs. This takes
 * whatever is selected in the catalog and lays it out on A4 in the grid a sheet
 * of adhesive labels actually comes in.
 *
 * Sizes are in millimetres throughout, because that is how label stock is sold
 * and how a printer's page setup is described. Pixels would be a translation
 * error waiting to happen.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { mouldQrValue } from '../../lib/qr.js';
import { Printer } from 'lucide-react';
import type { MouldRow } from '../../db/queries.js';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Field, Select } from '../../components/ui/field.js';

const PRINTING_CLASS = 'printing-label-sheet';

/**
 * Common adhesive label stock. Columns and rows are what fits on A4 with the
 * margins those sheets actually have.
 */
const LAYOUTS = {
  '24': { label: '24 per sheet - 63.5 × 33.9 mm', cols: 3, rows: 8, w: 63.5, h: 33.9 },
  '21': { label: '21 per sheet - 63.5 × 38.1 mm', cols: 3, rows: 7, w: 63.5, h: 38.1 },
  '12': { label: '12 per sheet - 63.5 × 72 mm', cols: 3, rows: 4, w: 63.5, h: 72 },
  '8': { label: '8 per sheet - 99.1 × 67.7 mm', cols: 2, rows: 4, w: 99.1, h: 67.7 },
} as const;

type LayoutKey = keyof typeof LAYOUTS;

export function LabelSheetDialog({
  moulds,
  open,
  onOpenChange,
}: {
  moulds: MouldRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [layoutKey, setLayoutKey] = useState<LayoutKey>('24');
  const [printing, setPrinting] = useState(false);
  const layout = LAYOUTS[layoutKey];
  const perSheet = layout.cols * layout.rows;
  const sheets = Math.max(1, Math.ceil(moulds.length / perSheet));

  useEffect(() => {
    if (!printing) return;
    document.body.classList.add(PRINTING_CLASS);
    const cleanup = () => {
      document.body.classList.remove(PRINTING_CLASS);
      setPrinting(false);
    };
    window.addEventListener('afterprint', cleanup, { once: true });
    const t = window.setTimeout(() => window.print(), 80);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('afterprint', cleanup);
      document.body.classList.remove(PRINTING_CLASS);
    };
  }, [printing]);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        size="sm"
        title={`Print ${moulds.length} label${moulds.length === 1 ? '' : 's'}`}
        description="Lays the selected moulds out on A4 for a sheet of adhesive labels."
        footer={
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setPrinting(true)} disabled={!moulds.length}>
              <Printer className="size-4" /> Print
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Label stock" hint="Match this to the sheets in the printer.">
            <Select
              value={layoutKey}
              onChange={(e) => setLayoutKey(e.target.value as LayoutKey)}
              options={Object.entries(LAYOUTS).map(([value, l]) => ({ value, label: l.label }))}
            />
          </Field>

          <p className="text-sm text-muted-foreground">
            {moulds.length} label{moulds.length === 1 ? '' : 's'} across {sheets} sheet
            {sheets === 1 ? '' : 's'} ({perSheet} per sheet).
          </p>

          {/* A preview of the first few, at the real aspect ratio. Getting label
              stock wrong wastes a whole sheet, so it is worth seeing. */}
          <div className="rounded-xl border border-border bg-surface-sunken/40 p-3">
            <div
              className="grid gap-1"
              style={{ gridTemplateColumns: `repeat(${Math.min(layout.cols, 3)}, 1fr)` }}
            >
              {moulds.slice(0, layout.cols).map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-2 rounded border border-border bg-surface p-1.5"
                  style={{ aspectRatio: `${layout.w} / ${layout.h}` }}
                >
                  <QRCodeSVG value={mouldQrValue(m)} size={30} level="M" />
                  <div className="min-w-0">
                    <div className="truncate font-mono text-[10px] font-bold">{m.assetCode}</div>
                    <div className="truncate text-[9px] text-muted-foreground">{m.name}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-subtle-foreground">Preview of the first row.</p>
          </div>
        </div>
      </Dialog>

      {printing
        ? createPortal(
            <div id="label-sheet">
              {Array.from({ length: sheets }, (_, sheet) => (
                <div
                  key={sheet}
                  className="label-page"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${layout.cols}, ${layout.w}mm)`,
                    gridAutoRows: `${layout.h}mm`,
                    justifyContent: 'center',
                    // Each sheet is its own page; the last one must not push a blank.
                    pageBreakAfter: sheet === sheets - 1 ? 'auto' : 'always',
                  }}
                >
                  {moulds.slice(sheet * perSheet, (sheet + 1) * perSheet).map((m) => (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2mm',
                        padding: '2mm',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                      }}
                    >
                      <QRCodeSVG
                        value={mouldQrValue(m)}
                        size={Math.round(layout.h * 2.1)}
                        level="M"
                        bgColor="#ffffff"
                        fgColor="#000000"
                      />
                      <div style={{ minWidth: 0, overflow: 'hidden' }}>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            fontSize: '3mm',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {m.assetCode}
                        </div>
                        <div
                          style={{
                            fontSize: '2.4mm',
                            color: '#333',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {m.name !== m.assetCode ? m.name : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
